/**
 * Letter Throwing Full Animation Variant (Web)
 *
 * Full letter throwing with chromatic layers and object pooling.
 * Uses RAF animation loop + CSS transforms for GPU acceleration.
 */

import { useEffect, useState, useMemo, useCallback, useRef, memo } from 'react';
import { View, Text, Pressable, Animated } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTranslation } from '@dramadetector/shared';
import type { LetterThrowingAnimationProps, AnimationPhase } from './types';
// PHASE_TIMING used indirectly via REVELATION_START from animationHelpers
import { useSpeedPresets } from './hooks/useSpeedPresets';
import FlyingLetter from './FlyingLetter.web';
import { getCategoryFromScore, getCategoryStyle } from '../../lib/categoryStyles';
import {
  POOL_SIZE,
  getPhaseFromProgress,
  animateBadgeReveal,
  REVELATION_START,
  ANIMATION_BG_GRADIENT,
  type PooledLetterState,
} from './animationHelpers.web';
import { styles } from './animationStyles.web';
import { useReducedMotionFallback } from './hooks/useReducedMotionFallback';
import DramaBadgeView from './DramaBadgeView.web';

const LetterThrowingFullAnimation = memo(function LetterThrowingFullAnimation({
  text,
  speed = '3',
  onComplete,
  onSkip,
  style,
  analysisResult,
}: LetterThrowingAnimationProps) {
  const { t } = useTranslation('home');
  const { settings, wordsForLine } = useSpeedPresets(speed);
  const prefersReducedMotion = useReducedMotionFallback();

  // State
  const [phase, setPhase] = useState<AnimationPhase>('idle');
  const [landedText, setLandedText] = useState('');
  const [progress, setProgress] = useState(0);
  const [showSkipHint, setShowSkipHint] = useState(false);

  // Badge animation
  const [badgeScale] = useState(new Animated.Value(0));
  const [badgeOpacity] = useState(new Animated.Value(0));

  // Object pool for flying letters
  const [pooledLetters, setPooledLetters] = useState<PooledLetterState[]>(() =>
    Array.from({ length: POOL_SIZE }, (_, i) => ({
      poolIndex: i,
      isActive: false,
      char: '',
      globalIndex: -1,
      key: `pool-${i}-init`,
    }))
  );
  const nextPoolIndexRef = useRef(0);
  const letterTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const rafIdRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const skipRequestedRef = useRef(false);
  const completionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const badgeRevealedRef = useRef(false);

  // Parse words
  const words = useMemo(() => text.split(/\s+/).filter(Boolean), [text]);
  const emergenceWords = useMemo(
    () => words.slice(0, wordsForLine),
    [words, wordsForLine]
  );
  const emergenceText = useMemo(() => emergenceWords.join(' '), [emergenceWords]);

  // Category styling
  const category = analysisResult?.category || getCategoryFromScore(analysisResult?.score || 50);
  const categoryStyle = getCategoryStyle(category);

  // Duration based on speed
  const totalDuration = useMemo(() => {
    const baseDuration = 12000;
    return baseDuration / settings.multiplier;
  }, [settings.multiplier]);

  /** Get next pool slot (round-robin) */
  const getNextPoolSlot = useCallback((): number => {
    const slot = nextPoolIndexRef.current;
    nextPoolIndexRef.current = (nextPoolIndexRef.current + 1) % POOL_SIZE;
    return slot;
  }, []);

  /** Handle letter throw */
  const handleLetterThrow = useCallback(
    (char: string, globalIndex: number) => {
      const poolIndex = getNextPoolSlot();
      const activationKey = `pool-${poolIndex}-${globalIndex}-${Date.now()}`;

      setPooledLetters((prev) => {
        const updated = [...prev];
        updated[poolIndex] = {
          poolIndex,
          isActive: true,
          char,
          globalIndex,
          key: activationKey,
        };
        return updated;
      });

      if (globalIndex === 0) {
        setPhase('emergence');
      }
    },
    [getNextPoolSlot]
  );

  /** Handle letter land */
  const handleLetterLand = useCallback((poolIndex: number, char: string) => {
    setLandedText((prev) => prev + char);
    setPooledLetters((prev) => {
      const updated = [...prev];
      updated[poolIndex] = { ...updated[poolIndex], isActive: false };
      return updated;
    });
  }, []);

  /** Schedule letter throws */
  useEffect(() => {
    if (prefersReducedMotion) {
      setLandedText(emergenceText);
      setProgress(1);
      onComplete?.();
      return;
    }

    startTimeRef.current = performance.now();
    let cumulativeDelay = 100;
    let globalLetterIdx = 0;

    for (let w = 0; w < emergenceWords.length; w++) {
      if (w > 0) {
        cumulativeDelay += settings.minWord;
        const spaceDelay = cumulativeDelay;
        letterTimeoutsRef.current.push(
          setTimeout(() => {
            if (!skipRequestedRef.current) {
              setLandedText((prev) => prev + ' ');
            }
          }, spaceDelay)
        );
      }

      const word = emergenceWords[w];
      for (let l = 0; l < word.length; l++) {
        const char = word[l];
        const throwTime = cumulativeDelay;
        const gIdx = globalLetterIdx;

        letterTimeoutsRef.current.push(
          setTimeout(() => {
            if (!skipRequestedRef.current) {
              handleLetterThrow(char, gIdx);
            }
          }, throwTime)
        );

        cumulativeDelay += settings.minLetter;
        globalLetterIdx++;
      }
    }

    const animate = (currentTime: number) => {
      if (skipRequestedRef.current) return;

      const elapsed = currentTime - startTimeRef.current;
      const newProgress = Math.min(elapsed / totalDuration, 1);
      setProgress(newProgress);

      const currentPhase = getPhaseFromProgress(newProgress);
      setPhase((prev) => {
        if (prev !== currentPhase) return currentPhase;
        return prev;
      });

      if (newProgress < 1) {
        rafIdRef.current = requestAnimationFrame(animate);
      } else {
        completionTimerRef.current = setTimeout(() => onComplete?.(), 600);
      }
    };

    rafIdRef.current = requestAnimationFrame(animate);
    const hintTimeout = setTimeout(() => setShowSkipHint(true), 1000);

    return () => {
      letterTimeoutsRef.current.forEach(clearTimeout);
      letterTimeoutsRef.current = [];
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      clearTimeout(hintTimeout);
      if (completionTimerRef.current) clearTimeout(completionTimerRef.current);
    };
  }, [emergenceWords, emergenceText, settings, totalDuration, handleLetterThrow, onComplete, prefersReducedMotion]);

  // Badge reveal animation
  useEffect(() => {
    if (badgeRevealedRef.current) return;
    if (progress >= REVELATION_START && analysisResult) {
      badgeRevealedRef.current = true;
      animateBadgeReveal(badgeScale, badgeOpacity).start();
    }
  }, [progress, analysisResult, badgeScale, badgeOpacity]);

  /** Handle skip */
  const handleSkip = useCallback(() => {
    skipRequestedRef.current = true;
    letterTimeoutsRef.current.forEach(clearTimeout);
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);

    setPooledLetters((prev) => prev.map((l) => ({ ...l, isActive: false })));
    setLandedText(emergenceText);
    setProgress(1);
    setPhase('complete');

    onSkip?.();
  }, [emergenceText, onSkip]);

  const phaseLabel = useMemo(() => {
    return (phase === 'emergence' || phase === 'transition' || phase === 'reading')
      ? t('home.modern.processing') : '';
  }, [phase, t]);

  return (
    <Pressable
      onPress={handleSkip}
      style={[styles.container, style]}
      accessibilityRole="button"
      accessibilityLabel={t('home.modern.skip_animation')}
    >
      <LinearGradient
        colors={ANIMATION_BG_GRADIENT}
        style={styles.gradient}
      >
        <View style={styles.content}>
          <View style={styles.flightZone}>
            {pooledLetters.map((letter) => (
              <View
                key={`pool-slot-${letter.poolIndex}`}
                style={[
                  styles.pooledLetterWrapper,
                  { opacity: letter.isActive ? 1 : 0 },
                ]}
              >
                {letter.isActive && letter.char && (
                  <FlyingLetter
                    key={letter.key}
                    char={letter.char}
                    index={letter.globalIndex}
                    speedSettings={settings}
                    onLand={() => handleLetterLand(letter.poolIndex, letter.char)}
                    targetPosition={{ x: 0, y: 100 }}
                  />
                )}
              </View>
            ))}
          </View>

          <View style={styles.accumulatedContainer}>
            <Text style={styles.accumulatedText}>{landedText}</Text>
          </View>

          <DramaBadgeView
            analysisResult={analysisResult}
            badgeOpacity={badgeOpacity}
            badgeScale={badgeScale}
            categoryStyle={categoryStyle}
          />

          <View style={styles.progressContainer}>
            <View style={[styles.progressBar, { width: `${progress * 100}%` }]} />
          </View>

          {phaseLabel && <Text style={styles.phaseLabel}>{phaseLabel}</Text>}
          {showSkipHint && phase !== 'complete' && (
            <Text style={styles.skipHint}>{t('home.modern.tap_to_skip')}</Text>
          )}
        </View>
      </LinearGradient>
    </Pressable>
  );
});

export default LetterThrowingFullAnimation;
